// Window-event names that coordinate the bottom-left "Assets" (folder system)
// panel with its toolbar toggle. Mirrors canvas-elements-events.ts so the two
// bottom-left panels stay consistent and mutually exclusive.
export const ASSET_FOLDERS_EVENTS = {
  // Toolbar -> panel: toggle open/closed.
  toggle: 'flowmuse:asset-folders-toggle',
  // Panel -> toolbar: broadcast open state (for active highlight + mutual close).
  state: 'flowmuse:asset-folders-state',
  // Any source -> panel: re-fetch folders + current folder contents (e.g. after
  // a node saves an asset into a folder).
  refresh: 'flowmuse:asset-folders-refresh',
  // Node -> panel: open the Assets tab in "pick for node" mode.
  pickStart: 'flowmuse:asset-folders-pick-start',
  // Panel -> node: the user clicked an asset while picking.
  pickResult: 'flowmuse:asset-folders-pick-result',
  // Panel -> node: picking was cancelled (closed panel / left tab / banner).
  pickCancel: 'flowmuse:asset-folders-pick-cancel',
} as const;

export type AssetFoldersEventName =
  (typeof ASSET_FOLDERS_EVENTS)[keyof typeof ASSET_FOLDERS_EVENTS];

/** Fire an asset-folders window event with the given detail (no-throw). */
export function dispatchAssetFoldersEvent(
  name: AssetFoldersEventName,
  detail?: Record<string, unknown>,
): void {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {}
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Tell the (possibly open) Assets panel that the library changed — e.g. a
 * generation finished or an upload was registered — so it reloads without a
 * manual refresh. Debounced to coalesce bursts (multi-image generations,
 * batches) into a single reload. No-op when the panel isn't mounted (the event
 * just has no listener), so this is cheap to call from anywhere.
 */
export function notifyAssetLibraryChanged(delayMs = 500): void {
  if (typeof window === 'undefined') return;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    dispatchAssetFoldersEvent(ASSET_FOLDERS_EVENTS.refresh);
  }, delayMs);
}

export type LibraryPickResult = {
  id?: string;
  src: string;
  rawSrc?: string;
  type: 'image' | 'video' | 'audio';
  name?: string;
  contentType?: string | null;
};

/**
 * Open the sidebar Assets tab in "pick for node" mode and resolve with the asset
 * the user clicks, or `null` if they cancel (close panel / leave tab / banner).
 * This replaces the standalone AssetPickerDialog for loading library assets.
 */
export function requestLibraryPick(opts?: {
  fileType?: 'image' | 'video' | 'audio' | 'model';
}): Promise<LibraryPickResult | null> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(null);
      return;
    }
    const requestId = `pick_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    let settled = false;
    const cleanup = () => {
      window.removeEventListener(ASSET_FOLDERS_EVENTS.pickResult, onResult as EventListener);
      window.removeEventListener(ASSET_FOLDERS_EVENTS.pickCancel, onCancel as EventListener);
    };
    const onResult = (e: Event) => {
      const d = (e as CustomEvent)?.detail as { requestId?: string; item?: LibraryPickResult };
      if (!d || d.requestId !== requestId || settled) return;
      settled = true;
      cleanup();
      resolve(d.item ?? null);
    };
    const onCancel = (e: Event) => {
      const d = (e as CustomEvent)?.detail as { requestId?: string };
      if (!d || d.requestId !== requestId || settled) return;
      settled = true;
      cleanup();
      resolve(null);
    };
    window.addEventListener(ASSET_FOLDERS_EVENTS.pickResult, onResult as EventListener);
    window.addEventListener(ASSET_FOLDERS_EVENTS.pickCancel, onCancel as EventListener);
    dispatchAssetFoldersEvent(ASSET_FOLDERS_EVENTS.pickStart, {
      requestId,
      fileType: opts?.fileType,
    });
  });
}
